import React, { useState } from 'react';
import styles from './AffiliateProfile.module.css';

type Affiliate = {
  name: string;
  email: string;
  code: string;
  date_created: string;
};

export default function AffiliateProfile() {
  const [email, setEmail] = useState('');
  const [affiliate, setAffiliate] = useState<Affiliate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Function to lookup affiliate by email
  const handleLookup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setAffiliate(null);
      setCopied(false);

      const response = await fetch(`/api/affiliates/email/${encodeURIComponent(email)}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No affiliate found with this email address');
        }
        throw new Error('Failed to fetch affiliate information');
      }

      const data = await response.json();
      setAffiliate(data as Affiliate);
    } catch (err) {
      setError(((err as any)?.message as string) || 'Failed to fetch affiliate information');
      console.error('Error fetching affiliate:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to generate affiliate URL
  const getAffiliateUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/buy?ref=${affiliate?.code || ''}`;
  };

  // Function to copy URL to clipboard
  const copyToClipboard = () => {
    const url = getAffiliateUrl();
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy URL:', err);
        setError('Failed to copy to clipboard');
      });
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.title}>Affiliate Profile</h1>

        <div className={styles.lookupSection}>
          <form onSubmit={handleLookup} className={styles.lookupForm}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Enter your affiliate email address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your-email@example.com"
                required
              />
            </div>

            <button type="submit" className={styles.lookupButton} disabled={loading}>
              {loading ? 'Looking up...' : 'Find My Profile'}
            </button>
          </form>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
          </div>
        )}

        {affiliate && (
          <div className={styles.profileSection}>
            <div className={styles.profileCard}>
              <h2>Welcome, {affiliate.name}!</h2>

              <div className={styles.profileInfo}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Email:</span>
                  <span className={styles.infoValue}>{affiliate.email}</span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Affiliate Code:</span>
                  <span className={styles.infoValue}>{affiliate.code}</span>
                </div>

                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Member Since:</span>
                  <span className={styles.infoValue}>
                    {new Date(affiliate.date_created).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className={styles.urlSection}>
                <h3>Your Affiliate URL</h3>
                <div className={styles.urlDisplay}>{getAffiliateUrl()}</div>

                <div className={styles.buttonGroup}>
                  <button className={styles.copyButton} onClick={copyToClipboard}>
                    {copied ? 'Copied!' : 'Copy URL to Clipboard'}
                  </button>

                  <a
                    href={getAffiliateUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.viewButton}
                  >
                    Visit Your Link
                  </a>
                </div>
              </div>

              <div className={styles.helpSection}>
                <h3>How to Use Your Link</h3>
                <ol className={styles.helpList}>
                  <li>Share your unique URL with potential customers</li>
                  <li>When someone purchases using your link, you'll earn commission</li>
                  <li>Track your referrals and earnings in your affiliate dashboard</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
